import { SettingsIcon } from "lucide-react";
import { useState } from "react";

import { useViewerHasPermission } from "@/auth/shared";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "@/components/base/dropdown-menu";
import { MenuSection } from "@/components/base/menu";
import type { PoolTeam, TournamentDivisionTeam } from "@/db/schema";
import { isDefined } from "@/utils/types";

import { EditPoolFormModal } from "./edit-pool";
import { EditSeedFormModal } from "./edit-seed";
import {
  ForfeitPoolTeamForm,
  UnforfeitPoolTeamForm,
} from "./forfeit-pool-team";
import { PromoteFromWaitlistForm } from "./promote-from-waitlist";
import { RemoveTeamForm } from "./remove-team";
import { TdSwapPlayersForm } from "./td-swap-players";
import { TransferToTournamentForm } from "./transfer-to-tournament";
import { UndoAbandonRefForm } from "./undo-abandon-ref";

enum ModalKind {
  Remove = 0,
  UndoAbandonedRef = 1,
  PromoteFromWaitlist = 2,
  EditSeed = 3,
  EditPool = 4,
  TransferToTournament = 5,
  SwapPlayers = 6,
  ForfeitPoolMatches = 7,
  UnforfeitPoolMatches = 8,
}

export type TeamControlsDropdownProps = {
  tournamentDivisionTeamId: number;
  poolTeam?: Pick<PoolTeam, "poolId">;
  abandonedRefId?: number | null;
} & Pick<TournamentDivisionTeam, "seed" | "status" | "forfeited">;

export function TeamControlsDropdown({
  tournamentDivisionTeamId,
  status,
  seed,
  poolTeam,
  abandonedRefId,
  forfeited,
}: TeamControlsDropdownProps) {
  const canUpdate = useViewerHasPermission({
    tournaments: ["update"],
  });

  const [activeModal, setActiveModal] = useState<ModalKind>();

  if (!canUpdate) {
    return null;
  }

  const makeModalOpenProps = (kind: ModalKind) => ({
    tournamentDivisionTeamId,
    isOpen: activeModal === kind,
    onOpenChange: (open: boolean) => {
      const next = open ? kind : undefined;

      setActiveModal(next);
    },
  });

  return (
    <>
      <DropdownMenu buttonIcon={<SettingsIcon />}>
        <MenuSection title="Team Controls">
          {status === "waitlisted" ? (
            <>
              <DropdownMenuItem
                onPress={() => setActiveModal(ModalKind.PromoteFromWaitlist)}
              >
                Promote
              </DropdownMenuItem>
              <DropdownMenuItem
                onPress={() => setActiveModal(ModalKind.TransferToTournament)}
              >
                Transfer to Tournament
              </DropdownMenuItem>
              <DropdownMenuItem
                onPress={() => setActiveModal(ModalKind.SwapPlayers)}
              >
                Swap Partners
              </DropdownMenuItem>
            </>
          ) : (
            <>
              <DropdownMenuItem
                onPress={() => setActiveModal(ModalKind.EditSeed)}
              >
                Edit Seed
              </DropdownMenuItem>
              <DropdownMenuItem
                onPress={() => setActiveModal(ModalKind.EditPool)}
              >
                Edit Pool
              </DropdownMenuItem>
              <DropdownMenuItem
                onPress={() => setActiveModal(ModalKind.TransferToTournament)}
              >
                Transfer to Tournament
              </DropdownMenuItem>
              <DropdownMenuItem
                onPress={() => setActiveModal(ModalKind.SwapPlayers)}
              >
                Swap Partners
              </DropdownMenuItem>
              <DropdownMenuItem
                onPress={() => setActiveModal(ModalKind.Remove)}
              >
                Remove Team
              </DropdownMenuItem>
              <DropdownMenuItem
                isDisabled={!isDefined(abandonedRefId)}
                onPress={() => setActiveModal(ModalKind.UndoAbandonedRef)}
              >
                Undo Abandoned Ref
              </DropdownMenuItem>
              {isDefined(poolTeam?.poolId) &&
                (forfeited ? (
                  <DropdownMenuItem
                    onPress={() =>
                      setActiveModal(ModalKind.UnforfeitPoolMatches)
                    }
                  >
                    Undo Forfeit
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onPress={() => setActiveModal(ModalKind.ForfeitPoolMatches)}
                  >
                    Forfeit Pool Matches
                  </DropdownMenuItem>
                ))}
            </>
          )}
        </MenuSection>
      </DropdownMenu>

      {activeModal === ModalKind.PromoteFromWaitlist &&
        status === "waitlisted" && (
          <PromoteFromWaitlistForm
            {...makeModalOpenProps(ModalKind.PromoteFromWaitlist)}
            tournamentDivisionTeamId={tournamentDivisionTeamId}
          />
        )}

      {activeModal === ModalKind.UndoAbandonedRef && abandonedRefId && (
        <UndoAbandonRefForm
          {...makeModalOpenProps(ModalKind.UndoAbandonedRef)}
          refId={abandonedRefId}
        />
      )}

      {activeModal === ModalKind.Remove && (
        <RemoveTeamForm
          {...makeModalOpenProps(ModalKind.Remove)}
          tournamentDivisionTeamId={tournamentDivisionTeamId}
        />
      )}

      {activeModal === ModalKind.EditSeed && (
        <EditSeedFormModal
          {...makeModalOpenProps(ModalKind.EditSeed)}
          tournamentDivisionTeamId={tournamentDivisionTeamId}
          target="division"
          seed={seed ?? 1}
        />
      )}

      {activeModal === ModalKind.EditPool && (
        <EditPoolFormModal
          {...makeModalOpenProps(ModalKind.EditPool)}
          tournamentDivisionTeamId={tournamentDivisionTeamId}
          poolId={poolTeam?.poolId}
        />
      )}

      {activeModal === ModalKind.TransferToTournament && (
        <TransferToTournamentForm
          {...makeModalOpenProps(ModalKind.TransferToTournament)}
          tournamentDivisionTeamId={tournamentDivisionTeamId}
        />
      )}

      {activeModal === ModalKind.SwapPlayers && (
        <TdSwapPlayersForm
          {...makeModalOpenProps(ModalKind.SwapPlayers)}
          tournamentDivisionTeamId={tournamentDivisionTeamId}
        />
      )}

      {activeModal === ModalKind.ForfeitPoolMatches && poolTeam?.poolId && (
        <ForfeitPoolTeamForm
          {...makeModalOpenProps(ModalKind.ForfeitPoolMatches)}
          poolId={poolTeam.poolId}
          teamId={tournamentDivisionTeamId}
        />
      )}

      {activeModal === ModalKind.UnforfeitPoolMatches && poolTeam?.poolId && (
        <UnforfeitPoolTeamForm
          {...makeModalOpenProps(ModalKind.UnforfeitPoolMatches)}
          poolId={poolTeam.poolId}
          teamId={tournamentDivisionTeamId}
        />
      )}
    </>
  );
}
